import { Suspense, useEffect } from "react";
import type { ComponentType } from "react";
import { Outlet, Navigate, useNavigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import type { RouteRecord } from "vite-react-ssg";

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import LanguageLayout from "@/components/LanguageLayout";
import ScrollToTop from "./components/ScrollToTop";
import Index from "./pages/Index";
import { newsSlugById } from "@/data/newsSlugMap";

// Alla canonical-slugs för nyheter (SV och EN delar samma slug per artikel
// idag, så 20 unique slugs prerendras × 2 språkträd = 40 artikelsidor).
const newsSlugs = Object.values(newsSlugById);

// Filmer-kategorier (slugs från Filmer.tsx slugToFilterKey). Båda språken
// har olika canonical-slugs men routen accepterar bägge — vi prerendrar
// allt så SEO-paritet med tiego.se URL:er behålls.
const filmerKategorier = [
  "outsourcing",
  "koncernredovisning",
  "leasing",
  "kommuner",
  "revision",
  "group-accounting",
  "municipalities",
  "auditing",
];

// Wrap a default-exporting page module so it works with react-router's `lazy`
// field (which expects a named `Component` export). Required for
// vite-react-ssg's collectAssets() to walk the route tree correctly.
const lazyDefault =
  (loader: () => Promise<{ default: ComponentType<unknown> }>) =>
  async () => {
    const mod = await loader();
    return { Component: mod.default };
  };

const queryClient = new QueryClient();

const RootLayout = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <ScrollToTop />
        <Suspense fallback={<div className="min-h-screen" />}>
          <Outlet />
        </Suspense>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

// Klient-side redirect i en data-route-värld. Vid prerender returnerar den
// null (=> tom HTML) och den faktiska redirecten sker via useEffect i
// browsern. För SEO-säkra redirects används Azure SWA staticwebapp.config.json.
const RedirectTo = ({ to }: { to: string }) => {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(to, { replace: true });
  }, [navigate, to]);
  return null;
};

// Routes lokaliserade under /:lang
const langChildren: RouteRecord[] = [
  { index: true, Component: Index },
  { path: "tga", lazy: lazyDefault(() => import("./pages/TGA")), entry: "src/pages/TGA.tsx" },
  { path: "tga/kundberattelser", lazy: lazyDefault(() => import("./pages/TGAKundberattelser")), entry: "src/pages/TGAKundberattelser.tsx" },
  { path: "tle", lazy: lazyDefault(() => import("./pages/TLE")), entry: "src/pages/TLE.tsx" },
  { path: "tle/kundberattelser", lazy: lazyDefault(() => import("./pages/TLEKundberattelser")), entry: "src/pages/TLEKundberattelser.tsx" },
  { path: "tle-implementation", lazy: lazyDefault(() => import("./pages/TLEImplementation")), entry: "src/pages/TLEImplementation.tsx" },
  { path: "integrationer", lazy: lazyDefault(() => import("./pages/Integrationer")), entry: "src/pages/Integrationer.tsx" },
  { path: "implementation", lazy: lazyDefault(() => import("./pages/Implementation")), entry: "src/pages/Implementation.tsx" },
  { path: "kommuner", lazy: lazyDefault(() => import("./pages/Kommuner")), entry: "src/pages/Kommuner.tsx" },
  { path: "export-api", lazy: lazyDefault(() => import("./pages/ExportAPI")), entry: "src/pages/ExportAPI.tsx" },
  { path: "nyheter", lazy: lazyDefault(() => import("./pages/Nyheter")), entry: "src/pages/Nyheter.tsx" },
  // Prerendra varje artikel som canonical slug-URL. getStaticPaths returnerar
  // hela relativ-path inklusive literal segment "nyheter/" enligt vite-react-ssg.
  {
    path: "nyheter/:id",
    lazy: lazyDefault(() => import("./pages/NyhetDetalj")),
    entry: "src/pages/NyhetDetalj.tsx",
    getStaticPaths: () => newsSlugs.map((s) => `nyheter/${s}`),
  },
  { path: "filmer", lazy: lazyDefault(() => import("./pages/Filmer")), entry: "src/pages/Filmer.tsx" },
  // Prerendra varje kategori-filter (SV + EN slugs).
  {
    path: "filmer/:kategori",
    lazy: lazyDefault(() => import("./pages/Filmer")),
    entry: "src/pages/Filmer.tsx",
    getStaticPaths: () => filmerKategorier.map((k) => `filmer/${k}`),
  },
  // Lovable slog ihop karriär/medarbetare: /karriar renderar den omarbetade
  // Medarbetare-sidan, /medarbetare redirectar dit.
  { path: "karriar", lazy: lazyDefault(() => import("./pages/Medarbetare")), entry: "src/pages/Medarbetare.tsx" },
  { path: "medarbetare", Component: () => <Navigate to="../karriar" replace /> },
  { path: "support", lazy: lazyDefault(() => import("./pages/Support")), entry: "src/pages/Support.tsx" },
  { path: "book-a-demo", lazy: lazyDefault(() => import("./pages/BookaDemo")), entry: "src/pages/BookaDemo.tsx" },
  // Behåll gammal /boka-demo som redirect för bakåtkompatibilitet
  { path: "boka-demo", Component: () => <Navigate to="../book-a-demo" replace /> },
  { path: "ai-policy", lazy: lazyDefault(() => import("./pages/AIPolicy")), entry: "src/pages/AIPolicy.tsx" },
];

export const routes: RouteRecord[] = [
  {
    path: "/",
    Component: RootLayout,
    children: [
      // Root → /sv
      { index: true, Component: () => <RedirectTo to="/sv" /> },

      // Old routes without lang prefix → /sv/...
      { path: "tga", Component: () => <RedirectTo to="/sv/tga" /> },
      { path: "tga/kundberattelser", Component: () => <RedirectTo to="/sv/tga/kundberattelser" /> },
      { path: "tle", Component: () => <RedirectTo to="/sv/tle" /> },
      { path: "tle/kundberattelser", Component: () => <RedirectTo to="/sv/tle/kundberattelser" /> },
      { path: "tle-implementation", Component: () => <RedirectTo to="/sv/tle-implementation" /> },
      { path: "integrationer", Component: () => <RedirectTo to="/sv/integrationer" /> },
      { path: "implementation", Component: () => <RedirectTo to="/sv/implementation" /> },
      { path: "kommuner", Component: () => <RedirectTo to="/sv/kommuner" /> },
      { path: "export-api", Component: () => <RedirectTo to="/sv/export-api" /> },
      { path: "nyheter", Component: () => <RedirectTo to="/sv/nyheter" /> },
      { path: "nyheter/:id", Component: () => <RedirectTo to="/sv/nyheter" /> },
      { path: "filmer", Component: () => <RedirectTo to="/sv/filmer" /> },
      { path: "karriar", Component: () => <RedirectTo to="/sv/karriar" /> },
      { path: "medarbetare", Component: () => <RedirectTo to="/sv/karriar" /> },
      { path: "support", Component: () => <RedirectTo to="/sv/support" /> },
      { path: "boka-demo", Component: () => <RedirectTo to="/sv/book-a-demo" /> },
      { path: "book-a-demo", Component: () => <RedirectTo to="/sv/book-a-demo" /> },
      { path: "ai-policy", Component: () => <RedirectTo to="/sv/ai-policy" /> },

      // Engelska slug-aliaser (matchar tiego.se /en/-URL:er)
      { path: "en/integrations", Component: () => <RedirectTo to="/en/integrationer" /> },
      { path: "en/municipalities-regions", Component: () => <RedirectTo to="/en/kommuner" /> },
      { path: "en/news", Component: () => <RedirectTo to="/en/nyheter" /> },
      { path: "en/news/:id", Component: () => <RedirectTo to="/en/nyheter" /> },
      { path: "en/videos", Component: () => <RedirectTo to="/en/filmer" /> },
      { path: "en/career", Component: () => <RedirectTo to="/en/karriar" /> },
      { path: "en/team", Component: () => <RedirectTo to="/en/karriar" /> },
      { path: "en/home", Component: () => <RedirectTo to="/en" /> },

      // Svenska aliaser för tiego.se /sv/-URL:er som skiljer sig
      { path: "sv/home", Component: () => <RedirectTo to="/sv" /> },

      // Language-prefixed routes — prerendra båda språkträden
      {
        path: ":lang",
        Component: LanguageLayout,
        getStaticPaths: () => ["sv", "en"],
        children: langChildren,
      },

      // Unsubscribe — exkluderas från SSG via vite.config.ts.includedRoutes
      { path: "unsubscribe", lazy: lazyDefault(() => import("./pages/Unsubscribe")), entry: "src/pages/Unsubscribe.tsx" },

      // Catch-all — NotFound
      { path: "*", lazy: lazyDefault(() => import("./pages/NotFound")), entry: "src/pages/NotFound.tsx" },
    ],
  },
];

export default routes;
